// 聊天框输入钩子函数

import { useRef, useEffect, useState, useCallback } from "react";
import { uploadFile } from "@/api/uploadfile";
import ToastEvent from "@/utils/toast-event";
import { inferImageContentType } from "@/utils/imageFile";
import type { Attachment } from "@/types/chat";

interface UseChatInputProps {
  input: string;
  setInput: (value: string) => void;
  isLoading: boolean;
  stop: () => void;
  attachments: Attachment[];
  setAttachments: (
    attachments: Attachment[] | ((prev: Attachment[]) => Attachment[])
  ) => void;
  handleSubmit: (e: React.FormEvent) => void;
  uploadQueue?: string[];
  addAttachments?: (files: File[]) => Promise<void>;
  removeAttachment?: (id: string) => void;
  clearAttachments?: () => void;
}

export interface UseChatInputReturn {
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  fileInputRef: React.RefObject<HTMLInputElement>;
  uploadQueue: string[];
  isUploading: boolean;
  handleInput: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  submitForm: (extraData?: { quote?: string }) => void;
  handleFileChange: (
    event: React.ChangeEvent<HTMLInputElement>,
    options?: { assumeImage?: boolean },
  ) => void;
  adjustHeight: () => void;
}

export function useChatInput({
  setInput,
  setAttachments,
  handleSubmit,
  attachments,
}: UseChatInputProps): UseChatInputReturn {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadQueue, setUploadQueue] = useState<Array<string>>([]);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const adjustHeight = useCallback(() => {}, []);

  useEffect(() => {
    if (textareaRef.current) {
      adjustHeight();
    }
  }, [adjustHeight]);

  // 处理输入变化
  const handleInput = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      setInput(event.target.value);
      adjustHeight();
    },
    [setInput, adjustHeight]
  );

  // 提交表单
  const submitForm = useCallback(
    (extraData?: { quote?: string }) => {
      // 收集所有附件的URL
      const files = attachments
        .filter((attach) => attach.url) // 只包含已上传完成的附件（有url）
        .map((attach) => attach.url);

      // 创建一个自定义事件对象，包含附件URL信息
      const customEvent = new Event("submit") as any;
      customEvent.files = files;
      customEvent.attachments = attachments.filter((attach) => attach.url);

      if (extraData?.quote) {
        customEvent.quote = extraData.quote;
      }

      // 调用handleSubmit并传入自定义事件
      handleSubmit(customEvent);

      // 清空附件列表
      setAttachments([]);

      // 保持输入框焦点
      textareaRef.current?.focus();
    },
    [handleSubmit, setAttachments, attachments]
  );

  // 处理文件选择变化
  const handleFileChange = useCallback(
    async (
      event: React.ChangeEvent<HTMLInputElement>,
      options?: { assumeImage?: boolean },
    ) => {
      const allFiles = Array.from(event.target.files || []);

      if (allFiles.length === 0) return;

      const MAX_SIZE = 20 * 1024 * 1024;
      const files = allFiles.filter((file) => {
        if (file.size > MAX_SIZE) {
          ToastEvent.error(`文件 "${file.name}" 超过 20MB 限制，已跳过`);
          return false;
        }
        return true;
      });

      // 如果过滤后没有有效文件，清空输入框并返回
      if (files.length === 0) {
        event.target.value = "";
        return;
      }

      // 设置上传中状态
      setIsUploading(true);
      // 设置上传队列，显示正在上传的文件
      setUploadQueue(files.map((file) => file.name));

      try {
        // 立即创建临时附件并添加到附件列表，这样用户可以看到上传中的文件
        const tempAttachments: Attachment[] = [];
        files.forEach((file) => {
          const tempId =
            Date.now().toString() +
            Math.random().toString(36).substr(2, 9) +
            Math.random().toString(36).substr(2, 9);
          const contentType =
            file.type ||
            (options?.assumeImage ? inferImageContentType(file) : "");
          const tempAttachment: Attachment = {
            id: tempId,
            name: file.name,
            size: file.size,
            contentType,
            url: "",
            originalFile: file,
          };
          tempAttachments.push(tempAttachment);
        });

        setAttachments((prev: Attachment[]) => [...prev, ...tempAttachments]);

        const uploadPromises = tempAttachments.map(async (tempAttachment) => {
          const formData = new FormData();
          const originalFile = tempAttachment.originalFile;
          if (!originalFile) return false;

          formData.append("file", originalFile);

          try {
            const response = await uploadFile(formData);

            if (response.status === 0 && response.data) {
              const remote = Array.isArray(response.data)
                ? response.data[0]
                : response.data;
              setAttachments((prev: Attachment[]) =>
                prev.map((attach: Attachment) => {
                  if (attach.id === tempAttachment.id) {
                    return {
                      ...attach,
                      name: attach.name,
                      url: remote.url,
                      size: attach.size,
                      contentType:
                        attach.contentType ||
                        remote.fileType ||
                        inferImageContentType(originalFile),
                      originalFile: undefined,
                    };
                  }
                  return attach;
                })
              );
              return true;
            } else {
              // 上传失败 (status != 0)，从附件列表中移除
              setAttachments((prev: Attachment[]) =>
                prev.filter(
                  (attach: Attachment) => attach.id !== tempAttachment.id
                )
              );
              ToastEvent.error(`文件上传失败: ${tempAttachment.name}`);
              return false;
            }
          } catch (error: any) {
            console.error(`文件上传错误 (${tempAttachment.name}):`, error);
            setAttachments((prev: Attachment[]) =>
              prev.filter(
                (attach: Attachment) => attach.id !== tempAttachment.id
              )
            );
            ToastEvent.error(
              `文件上传失败: ${tempAttachment.name} - ${
                error.message || "未知错误"
              }`
            );
            return false;
          }
        });

        // 等待所有上传完成
        await Promise.all(uploadPromises);
      } catch (error) {
        console.error("批量上传处理错误:", error);
      } finally {
        // 清空上传队列
        setUploadQueue([]);
        // 上传完成，设置状态为false
        setIsUploading(false);
        // 清空文件输入，允许再次选择相同文件
        event.target.value = "";
      }
    },
    [setAttachments]
  );

  return {
    textareaRef: textareaRef as React.RefObject<HTMLTextAreaElement>,
    fileInputRef: fileInputRef as React.RefObject<HTMLInputElement>,
    uploadQueue,
    isUploading,
    handleInput,
    submitForm,
    handleFileChange,
    adjustHeight,
  };
}
